import { JAIL_RESTRICTED_PATHS } from "./jail";

/**
 * Mock dead state for your profile until the API returns `is_dead` / `isDead`.
 * Set to `false` when the backend field is live.
 */
export const MOCK_MY_PROFILE_IS_DEAD = false;

/** Rebirth flow — full rebirth UI (not the global dead overlay). */
export const DEAD_ACCOUNT_REBIRTH_PATH = "/rebirth";

/** External site for creating a new account. */
export const PLAYMAFIA_CREATE_ACCOUNT_URL = "https://playmafia.io/get-started/";

export const DEAD_ACCOUNT_FUNERAL_BG_URL =
  "https://play-mafia.sfo3.cdn.digitaloceanspaces.com/carousel/funeral.png";

/**
 * Paths where dead accounts may use normal page UI (unstake, vault, own profile).
 */
export const DEAD_ACCOUNT_FUNCTIONAL_PATHS = [
  "/rank-activation",
  "/equipment",
  "/unstake-mafia",
  "/worth",
  "/vault",
  "/buy",
];

export const DEAD_ACCOUNT_ALLOWED_PATHS = [
  DEAD_ACCOUNT_REBIRTH_PATH,
  ...DEAD_ACCOUNT_FUNCTIONAL_PATHS,
];

/**
 * Game / ecosystem routes — visiting these while dead shows the full-screen dead page.
 * (Aligned with jail crime restrictions plus other contract interactions.)
 */
export const DEAD_ACCOUNT_RESTRICTED_PATHS = [
  ...JAIL_RESTRICTED_PATHS,
  "/",
  "/market",
  "/exchange",
  "/family",
  "/organized-crime",
  "/kill-initiation",
  "/kill-outcome",
  "/killskill",
  "/helper",
  "/injail",
  "/jail",
  "/detective-agency",
  "/safehouse",
  "/premium",
  "/perks",
  "/dao",
  "/metrics",
  "/map",
  "/game",
];

const pathMatches = (pathname: string, path: string) => {
  if (path === "/") {
    return pathname === "/";
  }

  // `/profile` is the landing route; only `/profile/:name` is allowed
  if (path === "/profile") {
    return (
      pathname.startsWith("/profile/") &&
      pathname.length > "/profile/".length
    );
  }

  return pathname === path || pathname.startsWith(`${path}/`);
};

export const isDeadAccountPathAllowed = (pathname: string): boolean => {
  if (pathMatches(pathname, DEAD_ACCOUNT_REBIRTH_PATH)) {
    return true;
  }

  if (
    pathname.startsWith("/profile/") &&
    pathname.length > "/profile/".length
  ) {
    return true;
  }

  return DEAD_ACCOUNT_FUNCTIONAL_PATHS.some((path) =>
    pathMatches(pathname, path)
  );
};

/** Minimal header + no sidebar (rebirth, unstake, equipment, dead gate). */
export const DEAD_ACCOUNT_MINIMAL_LAYOUT_PATHS = [
  DEAD_ACCOUNT_REBIRTH_PATH,
  "/rank-activation",
  "/equipment",
  "/unstake-mafia",
  "/worth",
];

export const shouldUseDeadAccountMinimalLayout = (
  pathname: string,
  isDead: boolean
): boolean => {
  if (!isDead) return false;

  if (
    DEAD_ACCOUNT_MINIMAL_LAYOUT_PATHS.some((path) =>
      pathMatches(pathname, path)
    )
  ) {
    return true;
  }

  return shouldShowDeadAccountFullscreen(pathname, isDead);
};

export const isDeadAccountRestrictedPath = (pathname: string): boolean => {
  if (isDeadAccountPathAllowed(pathname)) {
    return false;
  }

  return DEAD_ACCOUNT_RESTRICTED_PATHS.some((path) =>
    pathMatches(pathname, path)
  );
};

/** Full-screen dead page on any route that is not rebirth / unstake / vault / etc. */
export const shouldShowDeadAccountFullscreen = (
  pathname: string,
  isDead: boolean
): boolean => isDead && !isDeadAccountPathAllowed(pathname);
